import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { formatDate, formatMoney } from '../lib/format';

interface InvoiceRow {
  id: string;
  number: string;
  issue_date: string;
  due_date: string;
  status: string;
  total: number;
  amount_paid: number;
  currency: string;
  contact_name: string;
  kind: 'invoice' | 'credit_note';
}

export default function Invoices() {
  const { currentOrg } = useOrg();
  const [rows, setRows] = useState<InvoiceRow[]>([]);
  const [statusFilter, setStatusFilter] = useState<'all' | 'draft' | 'sent' | 'paid' | 'overdue' | 'void'>('all');
  const [kindFilter, setKindFilter] = useState<'invoice' | 'credit_note'>('invoice');

  useEffect(() => {
    if (!currentOrg) return;
    (async () => {
      const { data } = await supabase
        .from('dodobooks_invoices')
        .select('id, number, issue_date, due_date, status, total, amount_paid, currency, kind, dodobooks_contacts(name)')
        .eq('org_id', currentOrg.id)
        .order('issue_date', { ascending: false });
      setRows(((data ?? []) as any[]).map((r) => ({
        id: r.id, number: r.number, issue_date: r.issue_date, due_date: r.due_date,
        status: r.status, total: r.total, amount_paid: r.amount_paid, currency: r.currency,
        contact_name: r.dodobooks_contacts?.name ?? '—',
        kind: r.kind ?? 'invoice',
      })));
    })();
  }, [currentOrg]);

  const filtered = rows
    .filter((r) => r.kind === kindFilter)
    .filter((r) => statusFilter === 'all' || r.status === statusFilter);
  const totals = filtered.reduce((acc, r) => {
    acc.total += Number(r.total);
    acc.outstanding += Number(r.total) - Number(r.amount_paid);
    return acc;
  }, { total: 0, outstanding: 0 });

  return (
    <div className="flex flex-col gap-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="h-page">Invoices</h1>
          <p className="text-text-dim text-sm mt-1">Sales invoices to customers. Posts to AR + revenue + VAT output when sent.</p>
        </div>
        <Link to="/invoices/new" className="btn btn-primary"><Plus size={14} /> New invoice</Link>
      </header>

      <div className="flex gap-3 items-center flex-wrap">
        <div className="flex bg-surface border border-line rounded-md overflow-hidden">
          {(['invoice', 'credit_note'] as const).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setKindFilter(k)}
              className={
                'px-3 py-1.5 text-xs font-mono uppercase tracking-wider ' +
                (kindFilter === k ? 'bg-emerald/10 text-emerald' : 'text-text-mute hover:text-text')
              }
            >{k === 'invoice' ? 'Invoices' : 'Credit notes'}</button>
          ))}
        </div>
        <div className="flex bg-surface border border-line rounded-md overflow-hidden">
          {(['all', 'draft', 'sent', 'paid', 'overdue', 'void'] as const).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setStatusFilter(f)}
              className={
                'px-3 py-1.5 text-xs font-mono uppercase tracking-wider ' +
                (statusFilter === f ? 'bg-emerald/10 text-emerald' : 'text-text-mute hover:text-text')
              }
            >{f}</button>
          ))}
        </div>
        <div className="ml-auto text-sm text-text-dim">
          <span className="num text-text">{formatMoney(totals.outstanding)}</span> outstanding ·
          {' '}<span className="num text-text-dim">{formatMoney(totals.total)}</span> billed
        </div>
      </div>

      <div className="card p-0">
        {filtered.length === 0 ? (
          <div className="text-text-mute text-sm py-12 text-center">
            No invoices here. <Link to="/invoices/new" className="text-emerald hover:underline">Create a new one.</Link>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th className="px-5">Number</th>
                <th>Customer</th>
                <th>Issued</th>
                <th>Due</th>
                <th>Status</th>
                <th className="text-right">Outstanding</th>
                <th className="text-right pr-5">Total</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const outstanding = Number(r.total) - Number(r.amount_paid);
                return (
                  <tr key={r.id}>
                    <td className="px-5">
                      <Link to={`/invoices/${r.id}`} className="text-emerald hover:underline font-medium">{r.number}</Link>
                    </td>
                    <td>{r.contact_name}</td>
                    <td className="text-text-dim">{formatDate(r.issue_date)}</td>
                    <td className="text-text-dim">{formatDate(r.due_date)}</td>
                    <td><span className={`pill pill-${r.status}`}>{r.status}</span></td>
                    <td className="text-right num">{outstanding > 0 ? formatMoney(outstanding, r.currency) : '—'}</td>
                    <td className="text-right num pr-5">{formatMoney(r.total, r.currency)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
